import React, { useState, useEffect, useCallback } from 'react';
import { statisticsApi } from './statisticsApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';

const month_ukr = [
  'січень', 'лютий', 'березень', 'квітень', 'травень', 'червень',
  'липень', 'серпень', 'вересень', 'жовтень', 'листопад', 'грудень'
];

const day_ua = [
  { t: 'нд', col: 'red' },
  { t: 'пн' },
  { t: 'вт' },
  { t: 'ср' },
  { t: 'чт' },
  { t: 'пт' },
  { t: 'сб', col: 'red' }
];

const status_tabel = {
  'current': 'Не закритий',
  'closed': 'Закритий',
  'need_approve': 'Потребує затвердження'
};

/**
 * StatisticaAttendanceGroup
 *
 * Режими роботи:
 *  - Одна група: передати prop `group` (об'єкт групи з полями _id, number, children)
 *  - Всі групи:  передати prop `groups` (масив груп) та `kgGroup` (номер групи для API)
 *
 * Спільні props: kgId, periodStartYear, isArchive
 */
const StatisticaAttendanceGroup = ({ kgId, group, groups, kgGroup, periodStartYear, isArchive }) => {
  // Режим: 'single' — одна група, 'all' — всі групи
  const mode = group ? 'single' : 'all';



  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [attendanceInfo, setAttendanceInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // ─── Генерація переліку доступних місяців ───────────────────────────────────
  const generatePeriods = useCallback(() => {
    const MAX_LENGTH_PERIOD = 13;
    const MONTH_FINISH_PERIOD = 7;

    const curDate = new Date();
    const [curMonth, curYear] = [curDate.getMonth(), curDate.getFullYear()];

    let lengthPeriod;
    if (curYear > periodStartYear && curMonth > MONTH_FINISH_PERIOD) {
      lengthPeriod = MAX_LENGTH_PERIOD;
    } else if (curYear === periodStartYear && curMonth === 7) {
      lengthPeriod = 1;
    } else if (curMonth > MONTH_FINISH_PERIOD) {
      lengthPeriod = curMonth - (MONTH_FINISH_PERIOD - 1);
    } else {
      lengthPeriod = curMonth + (MONTH_FINISH_PERIOD - 1);
    }

    if (lengthPeriod < 0) return [];

    const iterCount = Math.min(lengthPeriod, MAX_LENGTH_PERIOD);
    const result = [];
    for (let iter = iterCount; iter > 0; iter--) {
      const y_iter = iter > 5 ? periodStartYear + 1 : periodStartYear;
      const m_iter_index = iter > 5 ? iter - 6 : iter + 6;
      result.push(`${m_iter_index + 1}.${y_iter}`);
    }
    return result;
  }, [periodStartYear]);

  useEffect(() => {
    const generatedPeriods = generatePeriods();
    setPeriods(generatedPeriods);
    if (generatedPeriods.length > 0) setSelectedPeriod(generatedPeriods[0]);
  }, [generatePeriods]);

  // ─── Обробка даних для режиму "одна група" ───────────────────────────────────
  const processChildrenData = useCallback((attendanceData, childrenData) => {
    const childrenWithNumbers = childrenData.map(child => {
      const childInGroup = group.children?.find(c => c.childId === child._id);
      return {
        ...child,
        smallNumber: childInGroup?.smallNumber || 'недоступне',
        number: childInGroup?.number || 'недоступне'
      };
    });

    const result = {
      group: {
        kg_id: attendanceData[0]?._id?.idKg,
        gr_number: attendanceData[0]?._id?.grNb,
        month: attendanceData[0]?._id?.month
      },
      status: attendanceData[0]?._id?.status,
      tabel: []
    };

    attendanceData.forEach(child => {
      const childInfo = childrenWithNumbers.find(c => c._id === child._id?.idChild);
      const arrDays = child.tabel.main.map((el, i) =>
        (el === 1 || child.tabel.first[i] === 1) ? 1 : -1
      );
      result.tabel.push({
        child: {
          firstName: child.childInfo.firstName[0],
          lastName: child.childInfo.lastName[0],
          smallNumber: childInfo?.smallNumber || 'недоступне',
          big_number: childInfo?.number || 'недоступне',
          id: child._id.idChild,
          tPl: child._id.tPl
        },
        perdays: arrDays,
        perdaysfirst: child.tabel.first
      });
    });

    result.tabel.sort((a, b) => a.child.lastName.localeCompare(b.child.lastName));
    return result;
  }, [group]);

  // ─── Обробка даних для режиму "всі групи" ───────────────────────────────────
  const processKgData = useCallback((res) => {
    if (!res.data || !Array.isArray(res.data) || res.data.length === 0) return null;

    const allGroupChildren = (groups || []).flatMap(gr => gr.children || []);

    const result = {
      group: {
        kg_id: res.data[0]._id.idKg,
        gr_number: res.data[0]._id.grNb,
        month: res.data[0]._id.month
      },
      status: res.data[0]._id.status,
      tabel: []
    };

    res.data.forEach(child => {
      const arrDays = child.tabel.main.map((el, i) =>
        (el === 1 || child.tabel.first[i] === 1) ? 1 : -1
      );

      let smNumb = 'недоступне';
      let bgNumber = 'недоступне';
      
      const grCur = groups.find(gr=>{
        const ch_rec_indx = gr.children.findIndex(ch=>ch.childId == child._id.idChild);
        if(ch_rec_indx > -1) return true;
        return false;
      });

      let grNumber = grCur?.number || '—';


      const fEl = allGroupChildren.find(el => el.childId === child._id.idChild);
      if (fEl?.smallNumber) smNumb = fEl.smallNumber;
      if (fEl?.number) bgNumber = fEl.number;

      const mr_info = child.childInfo.mr[0] &&
        child.childInfo.mr[0].reduce((acc, cur) =>
          acc || (cur?.statusMR?.result === 'true'), false);

      result.tabel.push({
        child: {
          firstName: child.childInfo.firstName[0],
          lastName: child.childInfo.lastName[0],
          kyivreg: child.childInfo.kyivreg[0]?.result === 'true' ? 'Зареєстрований' : 'Немає',
          mrinfo: mr_info ? 'Наявна' : 'Відсутня',
          smallNumber: smNumb,
          big_number: bgNumber,
          grNumber,
          id: child._id.idChild,
          tPl: child._id.tPl
        },
        perdays: arrDays,
        perdaysfirst: child.tabel.first
      });
    });

    result.tabel.sort((a, b) => a.child.lastName.localeCompare(b.child.lastName));
    return result;
  }, [groups]);

  // ─── Завантаження даних ──────────────────────────────────────────────────────
  const loadAttendanceData = useCallback(async () => {
    if (!selectedPeriod) return;

    setLoading(true);

    try {
      if (mode === 'single') {
        // Режим: одна група
        const mainData = await statisticsApi.getGroupAttendance(kgId, group.number, selectedPeriod, isArchive);
        if (mainData.data && Array.isArray(mainData.data)) {
          const tempData = await statisticsApi.getGroupAttendanceTemp(kgId, group.number, selectedPeriod, isArchive);
          const combinedData = [...mainData.data, ...(tempData.data || [])];

          if (combinedData.length > 0) {
            const childrenData = await statisticsApi.getGroupChildren(kgId, group._id, selectedPeriod);
            const processedData = processChildrenData(combinedData, childrenData.data || []);
            setAttendanceInfo(processedData);
          } else {
            setAttendanceInfo({ group: { gr_number: group.number } });
          }
        } else {
          setAttendanceInfo({ group: { gr_number: group.number } });
        }
      } else {
        // Режим: всі групи садочку
        const res = await statisticsApi.getKgAttendance(kgId, kgGroup, selectedPeriod, groups);
        if (res) {
          const processed = processKgData(res);

 
          setAttendanceInfo(processed || { group: { gr_number: kgGroup } });
        } else {
          setAttendanceInfo({ group: { gr_number: kgGroup } });
        }
      }
    } catch (error) {
      console.error('Error loading attendance data:', error);
      const fallbackNumber = mode === 'single' ? group.number : kgGroup;
      setAttendanceInfo({ group: { gr_number: fallbackNumber } });
    } finally {
      setLoading(false);
    }
  }, [kgId, group, groups, kgGroup, selectedPeriod, mode, processChildrenData, processKgData, isArchive]);

  useEffect(() => {
    if (selectedPeriod) loadAttendanceData();
  }, [selectedPeriod]);

  // ─── Сортування ─────────────────────────────────────────────────────────────
  const handleSort = (key) => {
    if (!attendanceInfo?.tabel) return;
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (columnKey !== sortConfig.key) return faSort;
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  const sortedData = React.useMemo(() => {
    if (!attendanceInfo?.tabel || !sortConfig.key) return attendanceInfo?.tabel || [];
    return [...attendanceInfo.tabel].sort((a, b) => {
      const aValue = a.child[sortConfig.key] ?? '';
      const bValue = b.child[sortConfig.key] ?? '';
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [attendanceInfo, sortConfig]);

  // ─── Компоненти рендеру ──────────────────────────────────────────────────────
  const NoDataInfo = () => (
    <div className="row">
      <div className="col-12 text-center py-4">
        <span className="text-muted">Статус: <b>Не має інформації за обраний період</b></span>
      </div>
    </div>
  );

  const AttendanceTable = () => {
    if (!attendanceInfo?.tabel) return <NoDataInfo />;

    const [month, year] = attendanceInfo.group.month.split('.');
    const daysInMonth = new Date(year, month, 0).getDate();

    const getDayInfo = (dayIndex) => {
      const day = new Date(year, month - 1, dayIndex + 1).getDay();
      return day_ua[day];
    };

    // ── Конфігурація колонок залежно від режиму ────────────────────────────────
    // sticky left зміщення для фіксованих колонок:
    //   mode=single: [Таб.№=0] [Прізвище=80px] [Ім'я=230px] [Відвідано=350px]
    //   mode=all:    [Група=0] [Таб.№=70px] [Прізвище=150px] [Ім'я=300px] [Відвідано=420px]
    const isAll = mode === 'all';

    const stickyBase = isAll
      ? { group: 0, smallNumber: 70, lastName: 150, firstName: 300, visited: 420 }
      : { smallNumber: 0, lastName: 80, firstName: 230, visited: 350 };

    const sortableColumns = [
      ...(isAll ? [{ key: 'grNumber', label: 'Група', width: '70px', left: stickyBase.group, sortable: true }] : []),
      { key: 'smallNumber', label: 'Таб. №', width: '80px', left: stickyBase.smallNumber, sortable: true },
      { key: 'lastName', label: 'Прізвище', width: isAll ? '150px' : '150px', left: stickyBase.lastName, sortable: true },
      { key: 'firstName', label: "Ім'я", width: '120px', left: stickyBase.firstName, sortable: true },
    ];

    // ── Заголовок ──────────────────────────────────────────────────────────────
    const renderTableHeaders = () => (
      <tr>
        {sortableColumns.map((column) => (
          <th
            key={column.key}
            style={{
              width: column.width,
              minWidth: column.width,
              cursor: column.sortable ? 'pointer' : 'default',
              position: 'sticky',
              top: 0,
              left: `${column.left}px`,
              backgroundColor: 'white',
              zIndex: 30,
              verticalAlign: 'middle',
              padding: '4px 8px',
            }}
            onClick={() => column.sortable && handleSort(column.key)}
            className="sortable-header"
          >
            <div className="d-flex align-items-center justify-content-between">
              {column.label}
              {column.sortable && (
                <FontAwesomeIcon
                  icon={getSortIcon(column.key)}
                  className="ms-1"
                  style={{ fontSize: '12px', opacity: sortConfig.key === column.key ? 1 : 0.3 }}
                />
              )}
            </div>
          </th>
        ))}

        {/* Колонка "Відвідано" */}
        <th
          style={{
            width: '80px',
            minWidth: '80px',
            position: 'sticky',
            top: 0,
            left: `${stickyBase.visited}px`,
            backgroundColor: 'white',
            zIndex: 30,
            verticalAlign: 'middle',
            borderRight: '2px solid #dee2e6',
            padding: '4px 8px',
          }}
        >
          <b>Відвідано</b>
        </th>

        {/* Дні місяця */}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const dayInfo = getDayInfo(i);
          return (
            <th
              key={i}
              style={{
                width: '40px',
                minWidth: '40px',
                fontSize: '11px',
                textAlign: 'center',
                padding: '4px 2px',
                verticalAlign: 'middle',
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
                zIndex: 20,
              }}
            >
              <div style={dayInfo.col ? { color: dayInfo.col } : {}}>
                <div style={{ fontWeight: 'bold' }}>{i + 1}</div>
                <div style={{ fontSize: '10px' }}>{dayInfo.t}</div>
              </div>
            </th>
          );
        })}
      </tr>
    );

    // ── Рядки дітей ───────────────────────────────────────────────────────────
    const renderChildRows = () => {
      const style_wobed = {
        background: '#ffc632',
        borderRadius: '2px',
        textAlign: 'center',
        fontSize: '12px',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        minHeight: '30px',
      };
      const style_stnd = {
        background: '#d4f7d4',
        borderRadius: '2px',
        textAlign: 'center',
        fontSize: '12px',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        minHeight: '30px',
      };

      return sortedData.map((child) => {
        const daysPresent = child.perdays.reduce((sum, day) => day === 1 ? sum + 1 : sum, 0);

        return (
          <tr key={child.child.id} style={{ borderBottom: '1px solid #e1dcdc' }}>
            {/* Колонка "Група" — тільки в режимі all */}
            {isAll && (
              <td style={{ position: 'sticky', left: `${stickyBase.group}px`, backgroundColor: 'white', verticalAlign: 'middle', zIndex: 2, padding: '4px 8px' }}>
                {child.child.grNumber}
              </td>
            )}

            {/* Таб. № */}
            <td style={{ position: 'sticky', left: `${stickyBase.smallNumber}px`, backgroundColor: 'white', verticalAlign: 'middle', zIndex: 2, padding: '4px 8px' }}>
              {child.child.smallNumber}
            </td>

            {/* Прізвище */}
            <td style={{ position: 'sticky', left: `${stickyBase.lastName}px`, backgroundColor: 'white', verticalAlign: 'middle', zIndex: 2, padding: '4px 8px' }}>
              {child.child.lastName}
            </td>

            {/* Ім'я */}
            <td style={{ position: 'sticky', left: `${stickyBase.firstName}px`, backgroundColor: 'white', verticalAlign: 'middle', zIndex: 2, padding: '4px 8px' }}>
              {child.child.firstName}
            </td>

            {/* Відвідано */}
            <td style={{ position: 'sticky', left: `${stickyBase.visited}px`, backgroundColor: 'white', fontWeight: 'bold', textAlign: 'center', verticalAlign: 'middle', zIndex: 2, borderRight: '2px solid #dee2e6', padding: '4px 8px' }}>
              {daysPresent}
            </td>

            {/* Дні */}
            {child.perdays.slice(0, daysInMonth).map((day, dayIndex) => {
              const isPresent = day === 1;
              const dayInfo = getDayInfo(dayIndex);
              let style = {};
              if (isPresent && child.child.tPl === 'without-bed') {
                style = style_wobed;
              } else if (isPresent) {
                style = style_stnd;
              } else if (dayInfo.col) {
                style = { color: dayInfo.col, fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: '30px' };
              } else {
                style = { fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: '30px' };
              }
              return (
                <td key={dayIndex} style={{ padding: '2px', verticalAlign: 'middle' }}>
                  <div style={style}>{isPresent ? 'п' : (day === -1 ? 'в' : '')}</div>
                </td>
              );
            })}
          </tr>
        );
      });
    };

    // ── Підсумковий рядок ─────────────────────────────────────────────────────
    const renderSummaryRow = () => {
      const daySums = Array.from({ length: daysInMonth }, (_, dayIndex) =>
        sortedData.reduce((sum, child) => child.perdays[dayIndex] === 1 ? sum + 1 : sum, 0)
      );
      const totalSum = daySums.reduce((sum, s) => sum + s, 0);

      return (
        <tr style={{ fontWeight: 'bold' }}>
          {/* Фіксовані колонки — кожна окремо зі своїм sticky left */}
          {sortableColumns.map((column, idx) => (
            <td
              key={column.key}
              style={{ position: 'sticky', left: `${column.left}px`, backgroundColor: 'white', verticalAlign: 'middle', zIndex: 2, padding: '4px 8px' }}
            >
              {idx === 0 ? 'Всього:' : ''}
            </td>
          ))}
          {/* Відвідано — загальна сума */}
          <td
            style={{ position: 'sticky', left: `${stickyBase.visited}px`, backgroundColor: 'white', textAlign: 'center', verticalAlign: 'middle', zIndex: 2, borderRight: '2px solid #dee2e6', padding: '4px 8px' }}
          >
            {totalSum}
          </td>
          {/* Суми по днях */}
          {daySums.map((sum, index) => {
            const dayInfo = getDayInfo(index);
            return (
              <td key={index} style={{ textAlign: 'center', padding: '2px', verticalAlign: 'middle', color: dayInfo.col || 'inherit' }}>
                {sum}
              </td>
            );
          })}
        </tr>
      );
    };

    return (
      <div>
        <div
          className="table-responsive"
          style={{ overflowX: 'auto', borderRadius: '8px', maxHeight: '70vh' }}
        >
          <table className="table table-sm mb-0" style={{ border: 'none', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead style={{ borderBottom: '2px solid black' }}>
              {renderTableHeaders()}
            </thead>
            <tbody>{renderChildRows()}</tbody>
            <tfoot style={{ borderTop: '1px solid #dee2e6' }}>
              {renderSummaryRow()}
            </tfoot>
          </table>
        </div>

        {/* Легенда */}
        <div className="row mt-3">
          <div className="col-12">
            <div className="d-flex align-items-center flex-wrap gap-3">
              <small className="text-muted">Кольорами позначені типи місць:</small>
              <div className="d-flex align-items-center gap-1">
                <span style={{ display: 'inline-block', width: '14px', height: '14px', background: '#ffc632', borderRadius: '2px' }}></span>
                <small>короткотривале</small>
              </div>
              <div className="d-flex align-items-center gap-1">
                <span style={{ display: 'inline-block', width: '14px', height: '14px', background: '#d4f7d4', borderRadius: '2px' }}></span>
                <small>повного дня</small>
              </div>
              <small className="text-muted">п - присутній, в - відсутній</small>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ─── Рендер ──────────────────────────────────────────────────────────────────
  return (
    <div className="container-fluid py-3">
      <div className="row">
        <div className="col-12">
          {/* Панель вибору місяця */}
          <div className="mb-4">
            <div className="d-flex align-items-center flex-wrap gap-3">
              <div className="d-flex align-items-center gap-2">
                <label className="form-label fw-semibold mb-0">Оберіть період:</label>
                <select
                  className="form-select"
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  value={selectedPeriod}
                  disabled={loading || periods.length === 0}
                  style={{ width: 'auto', minWidth: '200px' }}
                >
                  {periods.map((period, index) => (
                    <option key={index} value={period}>
                      {`${month_ukr[+period.split('.')[0] - 1]} ${period.split('.')[1]}`}
                    </option>
                  ))}
                </select>
              </div>

              {loading && (
                <div className="d-flex align-items-center">
                  <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                  <span>Завантаження даних...</span>
                </div>
              )}
            </div>
          </div>

          {attendanceInfo?.tabel ? <AttendanceTable /> : <NoDataInfo />}
        </div>
      </div>

      <style jsx>{`
        .sortable-header:hover { background-color: #f8f9fa !important; }
        .table th.sortable-header { transition: background-color 0.15s ease; }
        .table-responsive::-webkit-scrollbar { height: 8px; }
        .table-responsive::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
        .table-responsive::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 4px; }
        .table-responsive::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }
        .table > :not(caption) > * > * { border: none; }
        .table-striped > tbody > tr:nth-of-type(odd) > * { --bs-table-accent-bg: transparent; }
        .table-active { --bs-table-accent-bg: transparent; }
        td[style*="sticky"], th[style*="sticky"] {
          --bs-table-bg: white !important;
          --bs-table-accent-bg: white !important;
          background-color: white !important;
        }
      `}</style>
    </div>
  );
};

export default StatisticaAttendanceGroup;